/**
 * browser-orchestration-store.ts
 *
 * Browser-compliant implementation of OrchestrationStore using
 * localStorage, VersionedEnvelope (v3), In-Memory indexing,
 * Optimistic CAS checking, and BroadcastChannel deduplication.
 *
 * Invariant: processId is NEVER written or set by BrowserOrchestrationStore.
 */

import {
  ModelDefinition,
  ModelRuntime,
  RuntimeIntent,
  NodeCapability,
  NodeHeartbeat,
  ModelAssignment,
  OrchestrationEvent,
  RevisionConflictError,
  PoolRevisionConflictError,
} from '../contracts/model-orchestration-contracts.js';
import {
  OrchestrationStore,
  VersionedEnvelope,
  PoolRevision,
} from './orchestration-store.js';

export const CURRENT_SCHEMA_VERSION = 3;

export class BrowserOrchestrationStore implements OrchestrationStore {
  private readonly storageKeys = {
    models: 'orchestration_model_definitions_v3',
    runtimes: 'orchestration_runtimes_v3',
    intents: 'orchestration_intents_v3',
    capabilities: 'orchestration_capabilities_v3',
    heartbeats: 'orchestration_heartbeats_v3',
    assignments: 'orchestration_assignments_v3',
    events: 'orchestration_events_v3',
    poolRevisions: 'orchestration_pool_revisions_v3',
  };

  // In-Memory mirrors for fast access and testing
  private models = new Map<string, ModelDefinition>();
  private runtimes = new Map<string, ModelRuntime>();
  private intents = new Map<string, RuntimeIntent>();
  private capabilities = new Map<string, NodeCapability>();
  private heartbeats = new Map<string, NodeHeartbeat[]>();
  private assignments = new Map<string, ModelAssignment>();
  private events: OrchestrationEvent[] = [];
  private poolRevisions = new Map<string, PoolRevision>(); // `${nodeId}:${modelId}` -> PoolRevision

  private broadcastChannel?: BroadcastChannel;
  private readonly originId: string;
  private sequenceCounter = 0;

  constructor(originId?: string) {
    this.originId = originId || `browser-${Math.random().toString(36).slice(2, 10)}`;
    if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel('msng3r-orchestration-mesh');
      } catch {}
    }
    this.loadFromStorage();
  }

  getOriginId(): string {
    return this.originId;
  }

  private wrapEnvelope<T>(data: T): VersionedEnvelope<T> {
    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      updatedAt: new Date().toISOString(),
      data,
    };
  }

  private readEnvelope<T>(key: string): VersionedEnvelope<T> | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.schemaVersion === 'number' && 'data' in parsed) {
        return parsed as VersionedEnvelope<T>;
      }
    } catch {}
    return null;
  }

  private writeEnvelope<T>(key: string, data: T): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const envelope = this.wrapEnvelope(data);
      localStorage.setItem(key, JSON.stringify(envelope));
    } catch {}
  }

  private loadFromStorage(): void {
    // 1. Models
    const modelsEnv = this.readEnvelope<ModelDefinition[]>(this.storageKeys.models);
    if (modelsEnv?.data) {
      for (const m of modelsEnv.data) this.models.set(m.id, m);
    }

    // 2. Runtimes (Strip any processId to preserve semantic truth in browser)
    const runtimesEnv = this.readEnvelope<ModelRuntime[]>(this.storageKeys.runtimes);
    if (runtimesEnv?.data) {
      for (const r of runtimesEnv.data) {
        const sanitized: ModelRuntime = { ...r, processId: undefined };
        this.runtimes.set(sanitized.runtimeId, sanitized);
      }
    }

    // 3. Intents
    const intentsEnv = this.readEnvelope<RuntimeIntent[]>(this.storageKeys.intents);
    if (intentsEnv?.data) {
      for (const i of intentsEnv.data) this.intents.set(`${i.nodeId}:${i.modelId}`, i);
    }

    // 4. Capabilities
    const capEnv = this.readEnvelope<NodeCapability[]>(this.storageKeys.capabilities);
    if (capEnv?.data) {
      for (const c of capEnv.data) this.capabilities.set(c.nodeId, c);
    }

    // 5. Assignments
    const assignEnv = this.readEnvelope<ModelAssignment[]>(this.storageKeys.assignments);
    if (assignEnv?.data) {
      for (const a of assignEnv.data) this.assignments.set(a.id, a);
    }

    // 6. Events (Ring buffer)
    const eventsEnv = this.readEnvelope<OrchestrationEvent[]>(this.storageKeys.events);
    if (eventsEnv?.data) {
      this.events = eventsEnv.data;
    }

    // 7. Pool Revisions
    const poolEnv = this.readEnvelope<PoolRevision[]>(this.storageKeys.poolRevisions);
    if (poolEnv?.data) {
      for (const p of poolEnv.data) this.poolRevisions.set(`${p.nodeId}:${p.modelId}`, p);
    }
  }

  // ── Model Catalog ────────────────────────────────────────────────────────────

  async getModels(): Promise<readonly ModelDefinition[]> {
    return Array.from(this.models.values());
  }

  async saveModel(model: ModelDefinition): Promise<void> {
    this.models.set(model.id, model);
    this.writeEnvelope(this.storageKeys.models, Array.from(this.models.values()));
  }

  async deleteModel(id: string): Promise<void> {
    this.models.delete(id);
    this.writeEnvelope(this.storageKeys.models, Array.from(this.models.values()));
  }

  // ── Pool Revision ────────────────────────────────────────────────────────────

  async getPoolRevision(nodeId: string, modelId: string): Promise<PoolRevision> {
    const key = `${nodeId}:${modelId}`;
    return this.poolRevisions.get(key) ?? { nodeId, modelId, revision: 1 };
  }

  async savePoolRevision(revision: PoolRevision): Promise<void> {
    const key = `${revision.nodeId}:${revision.modelId}`;
    this.poolRevisions.set(key, revision);
    this.writeEnvelope(this.storageKeys.poolRevisions, Array.from(this.poolRevisions.values()));
  }

  // ── Runtime Records ──────────────────────────────────────────────────────────

  async getRuntimes(): Promise<readonly ModelRuntime[]> {
    return Array.from(this.runtimes.values());
  }

  async saveRuntime(runtime: ModelRuntime, expectedRevision?: number): Promise<void> {
    const existing = this.runtimes.get(runtime.runtimeId);
    if (expectedRevision !== undefined) {
      const currentRev = existing?.revision ?? 0;
      if (currentRev !== expectedRevision) {
        throw new RevisionConflictError(expectedRevision, currentRev);
      }
    }

    // Browser store NEVER assigns processId
    const sanitized: ModelRuntime = {
      ...runtime,
      processId: undefined,
      revision: (existing?.revision ?? 0) + 1,
    };

    this.runtimes.set(sanitized.runtimeId, sanitized);
    this.writeEnvelope(this.storageKeys.runtimes, Array.from(this.runtimes.values()));
  }

  async saveRuntimes(runtimes: readonly ModelRuntime[]): Promise<void> {
    for (const r of runtimes) {
      const existing = this.runtimes.get(r.runtimeId);
      const sanitized: ModelRuntime = {
        ...r,
        processId: undefined,
        revision: (existing?.revision ?? 0) + 1,
      };
      this.runtimes.set(sanitized.runtimeId, sanitized);
    }
    this.writeEnvelope(this.storageKeys.runtimes, Array.from(this.runtimes.values()));
  }

  async deleteRuntime(runtimeId: string): Promise<void> {
    this.runtimes.delete(runtimeId);
    this.writeEnvelope(this.storageKeys.runtimes, Array.from(this.runtimes.values()));
  }

  async deleteRuntimes(runtimeIds: readonly string[]): Promise<void> {
    for (const id of runtimeIds) {
      this.runtimes.delete(id);
    }
    this.writeEnvelope(this.storageKeys.runtimes, Array.from(this.runtimes.values()));
  }

  async scaleRuntimes(change: {
    readonly upsert: readonly ModelRuntime[];
    readonly deleteRuntimeIds: readonly string[];
    readonly poolRevision: PoolRevision;
  }): Promise<void> {
    // 1. Verify Pool CAS
    const poolKey = `${change.poolRevision.nodeId}:${change.poolRevision.modelId}`;
    const currentPool = this.poolRevisions.get(poolKey) ?? {
      nodeId: change.poolRevision.nodeId,
      modelId: change.poolRevision.modelId,
      revision: 1,
    };

    if (currentPool.revision !== change.poolRevision.revision) {
      throw new PoolRevisionConflictError(change.poolRevision.nodeId, change.poolRevision.modelId);
    }

    // 2. Perform Atomic In-Memory Commit
    for (const u of change.upsert) {
      const existing = this.runtimes.get(u.runtimeId);
      this.runtimes.set(u.runtimeId, {
        ...u,
        processId: undefined,
        revision: (existing?.revision ?? 0) + 1,
      });
    }

    for (const d of change.deleteRuntimeIds) {
      this.runtimes.delete(d);
    }

    const nextPoolRevision: PoolRevision = {
      ...change.poolRevision,
      revision: change.poolRevision.revision + 1,
    };
    this.poolRevisions.set(poolKey, nextPoolRevision);

    // 3. Single atomic persistence write
    this.writeEnvelope(this.storageKeys.runtimes, Array.from(this.runtimes.values()));
    this.writeEnvelope(this.storageKeys.poolRevisions, Array.from(this.poolRevisions.values()));
  }

  // ── Runtime Intents ──────────────────────────────────────────────────────────

  async getIntents(): Promise<readonly RuntimeIntent[]> {
    return Array.from(this.intents.values());
  }

  async saveIntent(intent: RuntimeIntent): Promise<void> {
    this.intents.set(`${intent.nodeId}:${intent.modelId}`, intent);
    this.writeEnvelope(this.storageKeys.intents, Array.from(this.intents.values()));
  }

  // ── Node Capabilities & Heartbeats ───────────────────────────────────────────

  async getNodeCapabilities(): Promise<readonly NodeCapability[]> {
    return Array.from(this.capabilities.values());
  }

  async saveNodeCapability(cap: NodeCapability): Promise<void> {
    this.capabilities.set(cap.nodeId, cap);
    this.writeEnvelope(this.storageKeys.capabilities, Array.from(this.capabilities.values()));
  }

  async appendHeartbeat(hb: NodeHeartbeat): Promise<void> {
    const list = this.heartbeats.get(hb.nodeId) || [];
    list.push(hb);
    if (list.length > 50) list.shift(); // Keep last 50
    this.heartbeats.set(hb.nodeId, list);
  }

  async getHeartbeatHistory(nodeId: string, limit: number = 50): Promise<readonly NodeHeartbeat[]> {
    const list = this.heartbeats.get(nodeId) || [];
    return list.slice(-limit);
  }

  // ── Assignments ──────────────────────────────────────────────────────────────

  async getAssignments(): Promise<readonly ModelAssignment[]> {
    return Array.from(this.assignments.values());
  }

  async saveAssignment(assignment: ModelAssignment): Promise<void> {
    this.assignments.set(assignment.id, assignment);
    this.writeEnvelope(this.storageKeys.assignments, Array.from(this.assignments.values()));
  }

  async deleteAssignment(id: string): Promise<void> {
    this.assignments.delete(id);
    this.writeEnvelope(this.storageKeys.assignments, Array.from(this.assignments.values()));
  }

  // ── Events ───────────────────────────────────────────────────────────────────

  async appendEvent(event: OrchestrationEvent): Promise<void> {
    const seq = ++this.sequenceCounter;
    const finalEvent: OrchestrationEvent = {
      ...event,
      originId: event.originId || this.originId,
      sequence: event.sequence || seq,
    };

    this.events.push(finalEvent);
    if (this.events.length > 1000) {
      this.events.shift();
    }

    this.writeEnvelope(this.storageKeys.events, this.events);

    // Broadcast across tabs
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(finalEvent);
      } catch {}
    }
  }

  async getRecentEvents(limit: number = 100): Promise<readonly OrchestrationEvent[]> {
    return this.events.slice(-limit);
  }

  clearAllForTesting(): void {
    this.models.clear();
    this.runtimes.clear();
    this.intents.clear();
    this.capabilities.clear();
    this.heartbeats.clear();
    this.assignments.clear();
    this.events = [];
    this.poolRevisions.clear();
    if (typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
  }
}
